<template>
  <div>
    <vx-card class="w-full">
      <div class="flex items-center">
        <h4>{{boardLabel}} 엑셀 업로드</h4>
        <span class="flex items-center"><ml-input class="ml-8 mr-2" style="width: 100px" type="number" v-model="header.year" :readonly="true" />년</span>
        <vs-button class="ml-auto" color="secondary" @click="onClickSave">등록</vs-button>
      </div>

      <!-- 기본정보 -->
      <FieldSelectModule
        title="작성자 정보"
        v-model="form.stdInfo"
        :typeslabel="{
          date: '등록일',
        }"
        :board="board"
        class="mt-28px"
      />

      <!-- 위험성평가 정보 -->
      <div class="mt-28px">
        <div class="flex justify-between items-center mt-6 mb-4">
          <h5><i class="dot"></i> 위험성평가 정보</h5>
        </div>
        <div class="writeGridContainer">
          <div class="writeGridLineItem">
            <div>파일 업로드</div>
            <div>
              <vs-button color="secondary" type="border" :href="excelLink">엑셀 템플릿 다운로드</vs-button>
              <vs-button color="secondary" class="ml-2" @click="onClickExcelUpload"> 작성 엑셀 업로드 </vs-button>
              <input v-show="false" ref="excelInput" type="file" multiple @change="onChangeReadFile" accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" />
            </div>
          </div>
        </div>
      </div>

      <div class="tip">
        <span class="tip-title">※ 작성요령</span>
        1. 실시시기 : 학교별 정기 위험성평가 실시 월(예시 2024-03)까지만 기재
        2. 실시방법(숫자기재) : 1. 학교자체실시, 2. 위험성평가만 위탁 실시, 3. 산업안전보건 전반 컨설팅 내 과업
        3. 업체명 : 위탁 실시의 경우 업체명 기재(자체실시의 경우 적지 않음)
        4. 직종 : 조리, 시설, 청소, 과학실험실, 사무실 실시 여부(O, X 표시)
        5. 위험성 감소대책 실행 건수 : 위험성평가표에서 조치 건수(위험성 개선) 기재
        6. 근로자 참여 : 근로자 참여 여부(O, X 표시)
        7. 근로자교육 : 근로자 교육 여부(O, X 표시)
        8. 비고
      </div>

      <div class="flex justify-between items-center mt-6 mb-4">
        <h5><i class="dot"></i> 등록 대상 교육 정보</h5>
        <div>
          <vs-button color="secondary" type="border" class="ml-2" @click="deleteRow"> 선택삭제 </vs-button>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="excel-table">
          <colgroup>
            <col width="50" />
            <col width="50" />
            <col width="140" />
            <col width="96" />
            <col width="180" />

            <col width="90" />
            <col width="90" />
            <col width="90" />
            <col width="90" />
            <col width="90" />

            <col width="120" />
            <col width="90" />
            <col width="90" />
            <col width="182" />
            <col width="93" />
          </colgroup>
          <thead>
          <tr>
            <th rowspan="2" >
              <vs-checkbox class="inline-flex" :checked="allCheck" @click.native="onClickAllCheckbox"/>
            </th>
            <th rowspan="2">No.</th>
            <th rowspan="2">실시시기<br>(예정)</th>
            <th rowspan="2">실시방법</th>
            <th rowspan="2">업체명</th>
            <th colspan="5">직종</th>
            <th rowspan="2">위험성 감소대책<br>실행 건수</th>
            <th rowspan="2">근로자 참여</th>
            <th rowspan="2">근로자 교육</th>
            <th rowspan="2">비고</th>
            <th rowspan="2">수정/삭제</th>
          </tr>
          <tr>
            <th>조리</th>
            <th>시설</th>
            <th>청소</th>
            <th>과학실험실</th>
            <th>사무실</th>
          </tr>
          </thead>
          <tbody v-for="(excel, excel_i) in excelData" :key="`${excel_i}_${excel[0]}`">
          <tr>
            <td>
              <vs-checkbox class="inline-flex" v-model="excel.isChecked" />
            </td>
            <td>{{ excel_i + 1 }}</td>
            <td v-for="head in heads" :key="`${excel_i}_td_${head}`">
              <div v-if="editExcelIndex === excel_i" class="p-1">
                <ml-input v-model="editExcelData.row[head]" class="w-full" />
              </div>
              <div v-else>
                <div class="flex items-center justify-center gap-2">
                  <p class="flex">
                    {{ excel.row[head] || "-" }}
                    <svg v-if="excel.err && excel.err[head]" style="margin-left:0.5rem;" width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.5 17C13.9183 17 17.5 13.4183 17.5 9C17.5 4.58172 13.9183 1 9.5 1C5.08172 1 1.5 4.58172 1.5 9C1.5 13.4183 5.08172 17 9.5 17Z" fill="#FFDADA"/>
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M10.3 5.8C10.3 5.35817 9.94178 5 9.49995 5C9.05812 5 8.69995 5.35817 8.69995 5.8V9C8.69995 9.44183 9.05812 9.8 9.49995 9.8C9.94178 9.8 10.3 9.44183 10.3 9V5.8ZM9.49597 11.4C9.05634 11.4 8.69995 11.7582 8.69995 12.2C8.69995 12.6418 9.05634 13 9.49597 13H9.50393C9.94356 13 10.3 12.6418 10.3 12.2C10.3 11.7582 9.94356 11.4 9.50393 11.4H9.49597Z" fill="#EA5455"/>
                    </svg>
                    <svg v-else-if="head === '학교명'" style="margin-left:0.5rem;"  width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.5 17C13.9183 17 17.5 13.4183 17.5 9C17.5 4.58172 13.9183 1 9.5 1C5.08172 1 1.5 4.58172 1.5 9C1.5 13.4183 5.08172 17 9.5 17Z" fill="#CAF9DF"/>
                      <path d="M14.1436 5.95214C14.0413 5.85511 13.9039 5.80078 13.7607 5.80078C13.6175 5.80078 13.48 5.85511 13.3778 5.95214L8.45419 10.824L6.48471 8.8117C6.43769 8.75101 6.37776 8.7008 6.30895 8.66448C6.24013 8.62817 6.16408 8.60658 6.0859 8.6012C6.00772 8.59582 5.92921 8.60676 5.85578 8.63329C5.78235 8.65982 5.71567 8.70133 5.66024 8.75498C5.60482 8.80863 5.56197 8.87317 5.53456 8.94425C5.50716 9.01533 5.49582 9.09126 5.50137 9.16694C5.50693 9.24262 5.52924 9.31627 5.56675 9.38288C5.60427 9.44949 5.65617 9.50753 5.71888 9.55305L8.1259 11.989C8.1259 12.0949 8.34478 12.2008 8.45419 12.2008C8.53912 12.2008 8.62285 12.1816 8.69881 12.1449C8.77477 12.1081 8.84085 12.0547 8.89181 11.989L14.1436 6.69352C14.2438 6.59453 14.3 6.46144 14.3 6.32283C14.3 6.18422 14.2438 6.05113 14.1436 5.95214Z" fill="#28C76F"/>
                    </svg>
                  </p>
                </div>
                <p v-if="excel.err && excel.err[head]" class="errMsg">{{ excel.err[head] }}</p>
              </div>
            </td>
            <td>
              <div class="flex justify-center gap-3">
                <fragment v-if="editExcelIndex === excel_i">
                  <button @click="onClickFinishExcelData(excel_i)">
                    <feather-icon icon="CheckIcon" svgClasses="h-5 w-5" />
                  </button>
                  <button @click="onClickCancelExcelData(excel_i)">
                    <feather-icon icon="XIcon" svgClasses="h-5 w-5" />
                  </button>
                </fragment>
                <fragment v-else>
                  <button @click="onClickEditExcelData(excel_i)">
                    <feather-icon icon="Edit2Icon" svgClasses="h-5 w-5" />
                  </button>
                  <button @click="onClickRemoveExcelData(excel_i)">
                    <feather-icon icon="Trash2Icon" svgClasses="h-5 w-5" />
                  </button>
                </fragment>
              </div>
            </td>
          </tr>
          <tr v-show="excel.open">
            <td colspan="11" class="p-0">
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </vx-card>
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import CompanySelector from "@/components/selector/CompanySelector.vue";
import tabItems from "@/views/boards/tab/tab-items";

import XLSX from "xlsx";
import config from "/public/config";
import FieldSelector from "@/components/selector/FieldSelector.vue";

export default {
  name: "PortExcelUpload",
  components: {
    FieldSelector,
    FieldSelectModule,
    CompanySelector,
  },
  props:{
    isPrint: Boolean,
    board: Object,
    header: Object,
  },
  data() {
    return {
      tabItems,
      form: {
        stdInfo: {},
      },

      excelData: [],

      editExcelIndex: null,
      editExcelData: null,

      fieldList: [],
    };
  },
  computed: {
    projectId() {
      return Number(this.$route.params.proj_id);
    },
    boardId() {
      return this.$route.params.boardId;
    },
    boardLabel() {
      return this.tabItems[this.boardId].label;
    },
    myInfo() {
      return this.$store.state.member.memberInfo;
    },
    excelLink() {
      return `${config.BASE_API_URL}/storage/app/media/%EC%9C%84%ED%97%98%EC%84%B1%ED%8F%89%EA%B0%80_%EC%8B%A4%EC%8B%9C%EA%B2%B0%EA%B3%BC_%EC%97%91%EC%85%80%EC%97%85%EB%A1%9C%EB%93%9C.xlsx`;
    },
    heads () {
      return ["실시시기", "실시방법", "업체명", "조리", "시설", "청소", "과학실험실", "사무실", "실행건수", "근로자참여", "근로자교육", "비고"];
    },
    validation () {
      if (this.excelData.length === 0) {
        return "엑셀 정보가 없습니다"
      } else if (!this.form.stdInfo || !this.form.stdInfo.field_name ) {
        return "학교(기관)명을 선택해주세요"
      } else {
        for (const excel of this.excelData) {
          if (Object.keys(excel.err).length > 0) {
            return "입력된 정보를 확인해주세요"
          }
        }
      }
      return true;
    },
    allCheck() {
      if (this.excelData.length > 0) {
        for (const excel of this.excelData) {
          if (!excel.isChecked) {
            return false;
          }
        }
      }
      return true
    },
  },
  async mounted() {
    await this.init();
  },
  methods: {
    async init() {
      // 학교(기관명) 체크 및 저장시 학교명 이 아닌 학교Id로 저장하기 위해 학교 리스트를 불러온다.
      let code = 'school'
      const options = {
        page: 1,
        perPage: 1000,
        projectId: this.projectId,
        code,
      }
      await this.$store.dispatch("project/LOAD_FIELD_LIST", options);

      const fieldListInfo = this.$store.state.project.fieldListInfo;
      this.fieldList = fieldListInfo.data;
    },
    getFieldId(fieldName) {
      if (this.fieldList.length > 0) {
        for (const field of this.fieldList) {
          if (field.field_name === fieldName) {
            return field.id;
          }
        }
      }
      return 0
    },
    getFieldName(fieldId) {
      if (this.fieldList.length > 0) {
        for (const field of this.fieldList) {
          if (field.id === fieldId) {
            return field.field_name;
          }
        }
      }
    },

    onClickExcelUpload() {
      const excelInput = this.$refs.excelInput;
      excelInput.value = null;
      excelInput.click();
    },
    onChangeReadFile(event) {
      this.readFile(event, (sheetData) => {
        const columns = ["A", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"];

        let rowIndex = 13;
        let rowData = [];
        do {
          rowData = this.getRowData(sheetData, rowIndex++, columns);
          if (!this.isEmptyRowData(rowData)) {
            const isChecked = false
            const row = { fieldId:0 }
            const err = {}
            for (let i = 0; i < this.heads.length; i++) {
              row[this.heads[i]] = rowData[i];
            }
            this.excelData.push({ isChecked, row, err })
          }
        } while (!this.isEmptyRowData(rowData));
      });
    },
    // 엑셀데이터 파싱
    async readFile(event, callback) {
      // get File object from input tag
      for (let i = 0; i < event.target.files.length; i++) {
        const file = event.target.files[i];
        // declare FileReader, temp result
        const reader = new FileReader();
        const tmpResult = {};

        // if you use "this", don't use "function(e) {...}"
        reader.onload = async (e) => {
          let data = e.target.result;
          data = new Uint8Array(data);
          // get excel file
          const excelFile = XLSX.read(data, { type: "array", cellDates: true });
          const sheetData = excelFile.Sheets[excelFile.SheetNames[0]];

          if (callback) {
            callback(sheetData)
          }
        };
        reader.readAsArrayBuffer(file);
      }
    },

    getCellData(sheet, cell, key = "v") {
      // sheet[row]가 존재하고, 해당 cell 존재하는지 확인
      // sheet[cell].v : Raw value
      // sheet[cell].w : Formatted value
      // sheet[cell].t : Type
      if (sheet[cell] !== undefined) {
        return sheet[cell][key];
      } else {
        return "";
      }
    },
    getRowData(sheet, row, columns, key = "v") {
      const result = [];
      for (const col in columns) {
        const cell = columns[col] + row;
        if (sheet[cell] !== undefined) {
          result.push(sheet[cell][key]);
        } else {
          result.push("");
        }
      }
      return result;
    },
    isEmptyRowData(row) {
      for (const cell of row) {
        if (cell !== "") {
          return false;
        }
      }
      return true
    },

    inputDetail(data) {
      this.editExcelData.form = data;
    },
    onClickEditExcelData(index) {
      this.editExcelIndex = index;
      this.editExcelData = JSON.parse(JSON.stringify(this.excelData[index]));
    },
    onClickRemoveExcelData(index) {
      this.excelData.splice(index, 1);
    },
    onClickFinishExcelData(index) {
      this.$set(this.excelData, index, this.editExcelData);
      this.editExcelIndex = null;
      this.editExcelData = null;
    },
    onClickCancelExcelData(index) {
      this.editExcelIndex = null;
      this.editExcelData = null;
    },
    onClickSave() {
      // 엑셀 데이터가 없을때 에러
      if (this.excelData.length < 1) {
        this.$emit("customAlert", "엑셀 정보가 없습니다");
        return;
      }

      if (this.validation !== true) {
        this.$emit("customAlert", this.validation);
        return;
      }

      const posts = this.excelData.map((ed) => {
        ed.row["학교명"] = this.getFieldId(ed.row["학교명"])
        const option = {
          stdInfo: this.form.stdInfo, // 작성자 정보
          ...ed.row,
        };
        return option;
      });

      this.$emit("save", {posts, callback: (success, data) => {
        if (success) {
          // 초기화
          this.excelData = [];
          this.editExcelData = [];
          this.editExcelIndex = null;
        }
      }});
    },
    // 정렬 후 기타, 기타 외를 배열의 마지막으로 이동하는 함수
    setCatOther(list) {
      list.sort();

      const etcIndex = list.indexOf("기타");
      if (etcIndex > -1) {
        list.splice(etcIndex, 1);
        list.push("기타");
      }
      const etcOutIndex = list.indexOf("기타 외");
      if (etcOutIndex > -1) {
        list.splice(etcOutIndex, 1);
        list.push("기타 외");
      }

      return list;
    },
    // 일괄 체크
    onClickAllCheckbox() {
      if (this.allCheck) {
        if (this.excelData.length > 0) {
          for (const excel of this.excelData) {
            excel.isChecked = false
          }
        }
      } else {
        if (this.excelData.length > 0) {
          for (const excel of this.excelData) {
            excel.isChecked = true
          }
        }
      }
    },
    deleteRow() {
      if (this.excelData.length > 0) {
        this.excelData = this.excelData.filter((excel) => !excel.isChecked);
      }
    }
  },
};
</script>

<style scoped lang="scss">
table {
  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0;
    text-align: center;
  }

  th {
    font-weight: 700;
    font-size: 14px;
    line-height: 17px;

    background: #fafafa;
    border-top: 1px solid #cdcdcd;
    border-bottom: 1px solid #cdcdcd;
    border-right: 1px solid #cdcdcd;
    padding: 16px 0;
  }
  th:first-child {
    border-left: 1px solid #cdcdcd;
  }

  td {
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;

    border-bottom: 1px solid #cdcdcd;
    border-right: 1px solid #cdcdcd;
    padding: 16px 0;
  }
  td:first-child {
    border-left: 1px solid #cdcdcd;
  }

  .sub-table {
    th {
      background: #7e72f20d;
      border-top: 0;
      padding: 16px 10px;
    }
  }

  button {
    width: 32px;
    height: 32px;

    display: flex;
    align-items: center;
    justify-content: center;

    color: #7e72f2;
    background: none;
    border: 1px solid #7e72f2;
    border-radius: 4px;
    cursor: pointer;

    span {
      margin: 0;
    }
  }

  .check {
    justify-content: center !important;
    ::v-deep {
      span {
        margin: 0;
      }
    }
  }
}

.tip{
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
  white-space: pre-line;
}

.tip-title {
  font-size: 14px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
}

.errMsg {
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  text-align: center;
  color: #ea5455;
  margin-top: 2px;
}
</style>

